import React, { useRef, useState } from 'react';
import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { normalizeRoutePage, routePageToDbPayload } from '@/lib/normalizeRoutePage';
import {
  deleteRouteImageFromUrl,
  resolveRouteImageUrl,
  uploadRouteImage,
} from '@/lib/routeImageStorage';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const EMPTY_FARE = { vehicle: '', amount: '' };
const EMPTY_FAQ = { question: '', answer: '' };

const EMPTY_FORM = {
  slug: '',
  from_city: '',
  to_city: '',
  title: '',
  meta_description: '',
  h1: '',
  intro_text: '',
  approx_distance_km: '',
  approx_duration: '',
  image_url: '',
  flat_fares: [
    { vehicle: 'Sedan', amount: '' },
    { vehicle: 'SUV', amount: '' },
  ],
  faqs: [{ ...EMPTY_FAQ }],
  is_active: true,
  sort_order: 0,
};

function rowToForm(row) {
  const route = normalizeRoutePage(row);
  return {
    slug: row.slug,
    from_city: row.from_city || '',
    to_city: row.to_city || '',
    title: row.title || '',
    meta_description: row.meta_description || '',
    h1: row.h1 || '',
    intro_text: row.intro_text || '',
    approx_distance_km: row.approx_distance_km ?? '',
    approx_duration: row.approx_duration || '',
    image_url: row.image_url || '',
    flat_fares:
      route.flatFares?.length >= 1
        ? route.flatFares.map((t) => ({ vehicle: t.vehicle, amount: String(t.amount) }))
        : [{ ...EMPTY_FARE }],
    faqs:
      route.faqs?.length >= 1
        ? route.faqs.map((f) => ({ question: f.question, answer: f.answer }))
        : [{ ...EMPTY_FAQ }],
    is_active: row.is_active !== false,
    sort_order: row.sort_order ?? 0,
  };
}

const RoutesTab = ({ routes, onRefresh }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileRef = useRef(null);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, flat_fares: [{ ...EMPTY_FARE }, { vehicle: 'SUV', amount: '' }], faqs: [{ ...EMPTY_FAQ }] });
    setPendingImage(null);
    setImagePreview(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm(rowToForm(row));
    setPendingImage(null);
    setImagePreview(row.image_url ? resolveRouteImageUrl(row.image_url, row.slug) : null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setPendingImage(null);
    setImagePreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Use JPG, PNG, or WEBP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be 5 MB or smaller');
      return;
    }
    setPendingImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const updateFare = (index, field, value) => {
    setForm((p) => ({
      ...p,
      flat_fares: p.flat_fares.map((t, i) => (i === index ? { ...t, [field]: value } : t)),
    }));
  };

  const addFare = () => {
    setForm((p) => ({ ...p, flat_fares: [...p.flat_fares, { ...EMPTY_FARE }] }));
  };

  const removeFare = (index) => {
    setForm((p) => ({
      ...p,
      flat_fares: p.flat_fares.filter((_, i) => i !== index),
    }));
  };

  const updateFaq = (index, field, value) => {
    setForm((p) => ({
      ...p,
      faqs: p.faqs.map((f, i) => (i === index ? { ...f, [field]: value } : f)),
    }));
  };

  const addFaq = () => {
    setForm((p) => ({ ...p, faqs: [...p.faqs, { ...EMPTY_FAQ }] }));
  };

  const removeFaq = (index) => {
    setForm((p) => ({
      ...p,
      faqs: p.faqs.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    if (!form.slug.trim() || !form.title.trim() || !form.h1.trim()) {
      toast.error('Slug, title, and H1 are required');
      return;
    }

    setSaving(true);
    try {
      let imageUrl = form.image_url || null;

      if (pendingImage) {
        if (editing?.image_url) {
          try {
            await deleteRouteImageFromUrl(editing.image_url);
          } catch {
            /* legacy static path — no storage object */
          }
        }
        imageUrl = await uploadRouteImage(form.slug.trim(), pendingImage);
      }

      const payload = routePageToDbPayload(form, imageUrl);
      const now = new Date().toISOString();

      if (editing) {
        const { error } = await supabase
          .from('route_pages')
          .update(payload)
          .eq('id', editing.id);
        if (error) throw error;
        toast.success('Route updated');
      } else {
        const { error } = await supabase.from('route_pages').insert({
          id: crypto.randomUUID(),
          ...payload,
          created_at: now,
        });
        if (error) throw error;
        toast.success('Route created');
      }

      closeModal();
      onRefresh();
    } catch (err) {
      toast.error(err.message || 'Failed to save route');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const label = row.title || row.slug;
    if (!window.confirm(`Delete route "${label}"? This cannot be undone.`)) return;

    try {
      if (row.image_url) {
        try {
          await deleteRouteImageFromUrl(row.image_url);
        } catch {
          /* static path or already removed */
        }
      }
      const { error } = await supabase.from('route_pages').delete().eq('id', row.id);
      if (error) throw error;
      toast.success('Route deleted');
      onRefresh();
    } catch (err) {
      toast.error(err.message || 'Failed to delete route');
    }
  };

  const toggleActive = async (row) => {
    try {
      const { error } = await supabase
        .from('route_pages')
        .update({ is_active: !row.is_active, updated_at: new Date().toISOString() })
        .eq('id', row.id);
      if (error) throw error;
      toast.success(row.is_active ? 'Route hidden from site' : 'Route visible on site');
      onRefresh();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const routeLabel = (row) => {
    if (row.from_city && row.to_city) return `${row.from_city} → ${row.to_city}`;
    if (row.from_city) return row.from_city;
    return row.h1 || row.slug;
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-xl text-[#1B2D5E]">Manage Routes</h2>
          <p className="text-sm text-[#888] font-body mt-1">SEO landing pages & homepage carousel</p>
        </div>
        <Button onClick={openAdd} className="bg-[#1B2D5E] hover:bg-[#E8651A] text-white font-body">
          <Plus size={18} className="mr-2" />
          Add Route
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Route</TableHead>
              <TableHead>Sort</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {routes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-[#888] py-10 font-body">
                  No routes yet. Run the one-time migration or add a route.
                </TableCell>
              </TableRow>
            ) : (
              routes.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium max-w-[200px] truncate">{row.title}</TableCell>
                  <TableCell className="text-sm text-gray-600">{routeLabel(row)}</TableCell>
                  <TableCell>{row.sort_order ?? 0}</TableCell>
                  <TableCell>
                    <Badge className={row.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {row.is_active ? 'Active' : 'Hidden'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => openEdit(row)}>
                      <PencilSimple size={16} className="mr-1" />
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleActive(row)}>
                      {row.is_active ? 'Hide' : 'Show'}
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(row)}>
                      <Trash size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={modalOpen} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? 'Edit Route' : 'Add Route'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2 font-body">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Slug</Label>
                <Input
                  className="mt-1"
                  value={form.slug}
                  onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
                  placeholder="rajkot-to-ahmedabad-taxi"
                  disabled={!!editing}
                />
              </div>
              <div>
                <Label>Sort order</Label>
                <Input
                  type="number"
                  className="mt-1"
                  value={form.sort_order}
                  onChange={(e) => setForm((p) => ({ ...p, sort_order: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>From city</Label>
                <Input className="mt-1" value={form.from_city} onChange={(e) => setForm((p) => ({ ...p, from_city: e.target.value }))} />
              </div>
              <div>
                <Label>To city</Label>
                <Input className="mt-1" value={form.to_city} onChange={(e) => setForm((p) => ({ ...p, to_city: e.target.value }))} />
              </div>
            </div>

            <div>
              <Label>Page title (SEO)</Label>
              <Input className="mt-1" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
            </div>
            <div>
              <Label>Meta description</Label>
              <Textarea className="mt-1" rows={2} value={form.meta_description} onChange={(e) => setForm((p) => ({ ...p, meta_description: e.target.value }))} />
            </div>
            <div>
              <Label>H1 heading</Label>
              <Input className="mt-1" value={form.h1} onChange={(e) => setForm((p) => ({ ...p, h1: e.target.value }))} />
            </div>
            <div>
              <Label>Intro text</Label>
              <Textarea className="mt-1" rows={4} value={form.intro_text} onChange={(e) => setForm((p) => ({ ...p, intro_text: e.target.value }))} />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Approx distance (km)</Label>
                <Input type="number" className="mt-1" value={form.approx_distance_km} onChange={(e) => setForm((p) => ({ ...p, approx_distance_km: e.target.value }))} />
              </div>
              <div>
                <Label>Approx duration</Label>
                <Input className="mt-1" value={form.approx_duration} onChange={(e) => setForm((p) => ({ ...p, approx_duration: e.target.value }))} placeholder="4–4.5 hours" />
              </div>
            </div>

            <div>
              <Label>Route image</Label>
              {imagePreview && (
                <img src={imagePreview} alt="" className="mt-2 h-32 w-full object-cover rounded-lg border border-gray-200" />
              )}
              <Input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="mt-2" onChange={handleImageSelect} />
              <p className="text-xs text-gray-500 mt-1">Uploads to Supabase Storage (route-images bucket).</p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Flat fares (homepage cards)</Label>
                <Button type="button" size="sm" variant="outline" onClick={addFare}>+ Add tier</Button>
              </div>
              <div className="space-y-2">
                {form.flat_fares.map((tier, i) => (
                  <div key={`fare-${i}`} className="flex gap-2 items-center">
                    <Input placeholder="Vehicle" value={tier.vehicle} onChange={(e) => updateFare(i, 'vehicle', e.target.value)} className="flex-1" />
                    <Input type="number" placeholder="₹" value={tier.amount} onChange={(e) => updateFare(i, 'amount', e.target.value)} className="w-28" />
                    {form.flat_fares.length > 1 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeFare(i)}>×</Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>FAQs</Label>
                <Button type="button" size="sm" variant="outline" onClick={addFaq}>+ Add FAQ</Button>
              </div>
              <div className="space-y-3">
                {form.faqs.map((faq, i) => (
                  <div key={`faq-${i}`} className="border border-gray-100 rounded-lg p-3 space-y-2">
                    <Input placeholder="Question" value={faq.question} onChange={(e) => updateFaq(i, 'question', e.target.value)} />
                    <Textarea placeholder="Answer" rows={2} value={faq.answer} onChange={(e) => updateFaq(i, 'answer', e.target.value)} />
                    {form.faqs.length > 1 && (
                      <Button type="button" size="sm" variant="ghost" className="text-red-600" onClick={() => removeFaq(i)}>Remove FAQ</Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm((p) => ({ ...p, is_active: v }))} />
              <Label>Active on website</Label>
            </div>

            <Button onClick={handleSave} disabled={saving} className="w-full bg-[#1B2D5E] hover:bg-[#E8651A]">
              {saving ? 'Saving…' : editing ? 'Update route' : 'Create route'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoutesTab;
